import { useCallback, useEffect, useRef, useState } from "react";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";

/**
 * A draggable + resizable floating window. Used by the attachment viewer's
 * "floating" mode so users can reposition and resize the preview while still
 * interacting with the page behind it.
 */
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const FloatingWindow = ({ children, onMouseDownHeader, header, initialWidth = 720, initialHeight = 540, className = "" }) => {
    const viewportW = typeof window !== "undefined" ? window.innerWidth : 1280;
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;

    const [size, setSize] = useState({
        width: Math.min(initialWidth, viewportW - 40),
        height: Math.min(initialHeight, viewportH - 40),
    });
    const [position, setPosition] = useState({
        x: Math.max(20, (viewportW - Math.min(initialWidth, viewportW - 40)) / 2),
        y: Math.max(20, (viewportH - Math.min(initialHeight, viewportH - 40)) / 3),
    });

    const dragRef = useRef(null);

    const handleDragStart = useCallback(
        (e) => {
            // Ignore drags that start on interactive controls inside the header.
            if (e.target.closest("button, a, input, .ant-segmented, .ant-checkbox-wrapper")) return;
            e.preventDefault();
            dragRef.current = { startX: e.clientX, startY: e.clientY, originX: position.x, originY: position.y };
            if (onMouseDownHeader) onMouseDownHeader();
        },
        [position, onMouseDownHeader],
    );

    useEffect(() => {
        const handleMove = (e) => {
            if (!dragRef.current) return;
            const dx = e.clientX - dragRef.current.startX;
            const dy = e.clientY - dragRef.current.startY;
            setPosition({
                x: clamp(dragRef.current.originX + dx, 0, window.innerWidth - 120),
                y: clamp(dragRef.current.originY + dy, 0, window.innerHeight - 60),
            });
        };
        const handleUp = () => {
            dragRef.current = null;
        };
        document.addEventListener("mousemove", handleMove);
        document.addEventListener("mouseup", handleUp);
        return () => {
            document.removeEventListener("mousemove", handleMove);
            document.removeEventListener("mouseup", handleUp);
        };
    }, []);

    return (
        <div
            className={`fixed z-[1001] ${className}`}
            style={{ left: position.x, top: position.y }}
            role="dialog"
            aria-modal="false"
            aria-label="Attachment floating window"
        >
            <ResizableBox
                width={size.width}
                height={size.height}
                onResize={(_e, data) => setSize({ width: data.size.width, height: data.size.height })}
                minConstraints={[320, 240]}
                maxConstraints={[window.innerWidth - 20, window.innerHeight - 20]}
                resizeHandles={["se", "e", "s"]}
                className="flex flex-col overflow-hidden rounded-lg bg-white shadow-2xl ring-1 ring-black/10"
            >
                <div className="flex h-full flex-col">
                    <div onMouseDown={handleDragStart} className="cursor-move border-b border-gray-100 bg-gray-50 px-3 py-2 select-none">
                        {header}
                    </div>
                    <div className="relative flex-1 overflow-hidden bg-gray-100">{children}</div>
                </div>
            </ResizableBox>
        </div>
    );
};

export default FloatingWindow;
